import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ThemeProvider as EmotionProvider, Global } from '@emotion/react';
import { darkTheme, getGlobalStyles, lightTheme, ThemeNames } from '@logz-ui/foundations';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { applyThemeToDocument, suppressTransitionsDuringThemeSwap } from './utils';
export const ThemeContext = createContext({
    theme: lightTheme,
    themeName: ThemeNames.Light,
    toggleTheme: () => { },
    setTheme: () => { },
});
export const ThemeProvider = ({ initialTheme = ThemeNames.Light, theme, children, targetDocument = document, withGlobalStyles = true, }) => {
    const [themeName, setThemeName] = useState(initialTheme);
    const activeTheme = useMemo(() => (theme ?? (themeName === ThemeNames.Light ? lightTheme : darkTheme)), [theme, themeName]);
    const globalStyles = useMemo(() => getGlobalStyles(activeTheme), [activeTheme]);
    const toggleTheme = useCallback(() => {
        setThemeName(prevTheme => (prevTheme === ThemeNames.Light ? ThemeNames.Dark : ThemeNames.Light));
    }, []);
    const setTheme = useCallback((theme) => {
        setThemeName(theme);
    }, []);
    const value = useMemo(() => ({
        theme: activeTheme,
        themeName: activeTheme.name,
        toggleTheme,
        setTheme,
    }), [activeTheme, toggleTheme, setTheme]);
    useEffect(() => {
        if (targetDocument?.documentElement) {
            applyThemeToDocument(targetDocument, activeTheme.name);
        }
    }, [activeTheme.name, targetDocument]);
    const isInitialThemeRender = useRef(true);
    useLayoutEffect(() => {
        if (isInitialThemeRender.current) {
            isInitialThemeRender.current = false;
            return undefined;
        }
        if (!targetDocument?.head)
            return undefined;
        const restoreTransitions = suppressTransitionsDuringThemeSwap(targetDocument);
        const timeoutId = window.setTimeout(restoreTransitions, 0);
        return () => {
            window.clearTimeout(timeoutId);
            restoreTransitions();
        };
    }, [activeTheme.name, targetDocument]);
    return (_jsxs(EmotionProvider, { theme: activeTheme, children: [withGlobalStyles && _jsx(Global, { styles: globalStyles }), _jsx(ThemeContext.Provider, { value: value, children: children })] }));
};
export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
//# sourceMappingURL=ThemeProvider.js.map